import { useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  applyTheme,
  readThemePreference,
  subscribeThemePreference,
  watchSystemTheme,
  type ThemePreference,
} from '@/lib/theme';

const options: { value: ThemePreference; icon: typeof Sun; labelKey: string }[] = [
  { value: 'light', icon: Sun, labelKey: 'settings.theme.light' },
  { value: 'dark', icon: Moon, labelKey: 'settings.theme.dark' },
  { value: 'system', icon: Monitor, labelKey: 'settings.theme.system' },
];

export function ThemeToggle() {
  const { t } = useTranslation();
  const preference = useSyncExternalStore(subscribeThemePreference, readThemePreference);

  useEffect(() => {
    applyTheme(preference);
    if (preference !== 'system') return;
    return watchSystemTheme(() => applyTheme('system'));
  }, [preference]);

  return (
    <div
      role="group"
      aria-label={t('settings.appearance')}
      className="inline-flex gap-1 rounded-lg border border-line bg-surface p-1"
    >
      {options.map(({ value, icon: Icon, labelKey }) => (
        <Button
          key={value}
          size="icon"
          variant={preference === value ? 'primary' : 'ghost'}
          aria-pressed={preference === value}
          aria-label={t(labelKey)}
          title={t(labelKey)}
          onClick={() => applyTheme(value)}
        >
          <Icon aria-hidden="true" className="size-4" />
        </Button>
      ))}
    </div>
  );
}
